import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiException } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Spinner } from '../../components/ui/spinner';
import { Modal } from '../../components/ui/modal';
import { ArrowRight, ArrowLeft, AlertTriangle, UserCircle } from 'lucide-react';

interface Pendencia {
  id: string; tipo: string; descricao: string; status: string;
}
interface EsteiraItem {
  id: string;
  fila: string;
  prioridade: number;
  status: string;
  associadoId: string;
  agenteId: string;
  contratoId: string | null;
  abriuEm: string;
  associado?: { id: string; nome: string; documentoMask: string };
  pendencias?: Pendencia[];
}

// Ordem e rótulo das colunas (os dois fluxos lado a lado)
const COLUNAS: { fila: string; label: string; cor: string }[] = [
  { fila: 'analise',              label: 'Análise',              cor: 'border-blue-300' },
  { fila: 'tesouraria_novos',     label: 'Tesouraria (Novos)',   cor: 'border-green-300' },
  { fila: 'renovacao_aptos',      label: 'Renovação · Aptos',    cor: 'border-purple-300' },
  { fila: 'renovacao_validacao',  label: 'Renovação · Coordenação', cor: 'border-amber-300' },
  { fila: 'tesouraria_pagamentos',label: 'Tesouraria (Pgto)',    cor: 'border-teal-300' },
];

const tipoPendencia = [
  'documento_faltando','dado_invalido','divergencia_etipi',
  'aguardando_ligacao','aguardando_averbacao','aguardando_aprovacao','outro',
];
const FILAS_RETROCEDEM = new Set(['tesouraria_novos', 'renovacao_validacao', 'tesouraria_pagamentos']);

const pendLabel: Record<string, string> = {
  documento_faltando: 'Documento faltando',
  dado_invalido: 'Dado inválido',
  divergencia_etipi: 'Divergência ETIPI',
  aguardando_ligacao: 'Aguardando ligação',
  aguardando_averbacao: 'Aguardando averbação',
  aguardando_aprovacao: 'Aguardando aprovação',
  outro: 'Outro',
};

export function EsteiraKanbanPage() {
  const qc = useQueryClient();
  const [pendenciando, setPendenciando] = useState<EsteiraItem | null>(null);
  const [tipoPend, setTipoPend] = useState('documento_faltando');
  const [descPend, setDescPend] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const LOTE = 15;
  const [visiveis, setVisiveis] = useState<Record<string, number>>({});
  const getVis = (fila: string) => visiveis[fila] ?? LOTE;

  const { data, isLoading } = useQuery({
    queryKey: ['esteira'],
    queryFn: () => api<{ items: EsteiraItem[]; total: number }>('/esteira?pageSize=2000'),
    refetchInterval: 15000,
  });

  const avancar = useMutation({
    mutationFn: (id: string) => api(`/esteira/${id}/avancar`, { method: 'POST', body: {} }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['esteira'] }),
    onError: (e) => setErro(e instanceof ApiException ? e.message : 'Erro ao avançar'),
  });

  const retroceder = useMutation({
    mutationFn: (id: string) => api(`/esteira/${id}/retroceder`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['esteira'] }),
    onError: (e: any) => setErro(e?.apiError?.message ?? 'Não foi possível retroceder'),
  });

  const pendenciar = useMutation({
    mutationFn: (vars: { id: string; tipo: string; descricao: string }) =>
      api(`/esteira/${vars.id}/pendenciar`, { method: 'POST', body: { tipo: vars.tipo, descricao: vars.descricao } }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['esteira'] }); setPendenciando(null); setDescPend(''); },
    onError: (e) => setErro(e instanceof ApiException ? e.message : 'Erro ao pendenciar'),
  });

  if (isLoading) return <div className="flex justify-center p-12"><Spinner /></div>;

  const items = data?.items ?? [];
  const porFila = (fila: string) => items.filter((i) => i.fila === fila && i.status !== 'resolvido' && i.status !== 'cancelado');

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Esteira de Operações</h1>
        <p className="text-sm text-muted-foreground">
          Fluxo Novo: Análise → Tesouraria. Renovação: Aptos → Coordenação → Tesouraria.
        </p>
      </div>

      {erro && (
        <div className="mb-4 rounded bg-red-50 border border-red-200 p-3 text-sm text-red-800">
          {erro} <button className="ml-2 underline" onClick={() => setErro(null)}>fechar</button>
        </div>
      )}

      <div className="flex gap-4 overflow-x-auto pb-4">
        {COLUNAS.map((col) => {
          const cards = porFila(col.fila);
          return (
            <div key={col.fila} className="min-w-[280px] flex-1">
              <div className="mb-2 flex items-center justify-between">
                <h2 className="text-sm font-semibold">{col.label}</h2>
                <span className="text-xs rounded-full bg-gray-100 px-2 py-0.5">{cards.length}</span>
              </div>
              <div className="space-y-2 max-h-[calc(100vh-240px)] overflow-y-auto pr-1">
                {cards.length === 0 && (
                  <div className="rounded border border-dashed border-gray-200 p-4 text-center text-xs text-gray-400">vazio</div>
                )}
                {cards.slice(0, getVis(col.fila)).map((item) => {
                  const temPend = (item.pendencias?.length ?? 0) > 0;
                  return (
                    <Card key={item.id} className={`border-l-4 ${col.cor}`}>
                      <CardContent className="p-3">
                        <div className="flex items-start gap-2">
                          <UserCircle className="h-4 w-4 mt-0.5 text-gray-400" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{item.associado?.nome ?? `Associado ${item.associadoId}`}</p>
                            <p className="text-xs text-gray-500">{item.associado?.documentoMask}</p>
                          </div>
                        </div>
                        {temPend && (
                          <div className="mt-2 flex items-center gap-1 text-xs text-amber-700">
                            <AlertTriangle className="h-3 w-3" /> {item.pendencias!.length} pendência(s)
                          </div>
                        )}
                        <div className="mt-3 flex gap-2">
                          {FILAS_RETROCEDEM.has(item.fila) && (
                            <Button size="sm" variant="ghost" title="Voltar etapa"
                              disabled={retroceder.isPending}
                              onClick={() => retroceder.mutate(item.id)}>
                              <ArrowLeft className="h-3 w-3" />
                            </Button>
                          )}
                          <Button size="sm" variant="outline" className="flex-1"
                            disabled={avancar.isPending || temPend}
                            onClick={() => avancar.mutate(item.id)}>
                            <ArrowRight className="h-3 w-3 mr-1" /> Avançar
                          </Button>
                          <Button size="sm" variant="ghost" title="Abrir pendência"
                            onClick={() => { setPendenciando(item); setErro(null); }}>
                            <AlertTriangle className="h-3 w-3" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
                {cards.length > getVis(col.fila) && (
                  <button
                    className="w-full rounded border border-dashed border-gray-300 dark:border-gray-600 py-2 text-xs text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800"
                    onClick={() => setVisiveis((v) => ({ ...v, [col.fila]: getVis(col.fila) + LOTE }))}
                  >
                    Ver mais {Math.min(LOTE, cards.length - getVis(col.fila))} (de {cards.length})
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {pendenciando && (
        <Modal open onClose={() => setPendenciando(null)} title="Abrir pendência">
          <div className="space-y-3">
            <p className="text-sm">{pendenciando.associado?.nome}</p>
            <select className="w-full rounded border p-2 text-sm" value={tipoPend} onChange={(e) => setTipoPend(e.target.value)}>
              {tipoPendencia.map((t) => <option key={t} value={t}>{pendLabel[t] ?? t}</option>)}
            </select>
            <textarea className="w-full rounded border p-2 text-sm" rows={3} placeholder="Descreva a pendência"
              value={descPend} onChange={(e) => setDescPend(e.target.value)} />
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" onClick={() => setPendenciando(null)}>Cancelar</Button>
              <Button disabled={descPend.length < 5 || pendenciar.isPending}
                onClick={() => pendenciar.mutate({ id: pendenciando.id, tipo: tipoPend, descricao: descPend })}>
                Pendenciar
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
